import React from 'react';
import { connect } from 'react-redux';
import { useFeatureValue } from '@growthbook/growthbook-react';

import { deepClone } from '@gumtree/shared/src/model/generic.model';
import { ExperimentVariant, GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { Container, Grid } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';

import { copyright, globalSites, sections as footerSections, apps } from './footer-data.json';
import FooterCopyright from './footer-copyright';
import FooterLinks from './footer-links';
import FooterAppLinks from './footer-app-links';
import FooterSocial from './footer-social';
import ModernFooter from './modern-footer';
import { trackAndNavigate } from '../common/common-actions';
import ErrorBoundary from '../error-boundary';
import type { ShellState } from '../reducers/common';

import { FooterBlock, FooterDividerStyle } from './style';

const Divider = () => (
    <Grid container>
        <Grid item col={12} colXl={10} offsetXl={1}>
            <div css={FooterDividerStyle} />
        </Grid>
    </Grid>
);

export const Footer: React.FC<Props> = ({ category, device, trackAndNavigate }) => {
    const sections = deepClone(footerSections);
    const mhcVariant = useFeatureValue(GrowthBookFeature.MHC, ExperimentVariant.VARIANT_A);
    const votVariant = useFeatureValue(
        device.type === 'mobile' ? GrowthBookFeature.VIP_M : GrowthBookFeature.VIP_PC,
        ExperimentVariant.VARIANT_A
    );
    const isVotRedesign = votVariant === ExperimentVariant.VARIANT_C;
    const showModernFooter =
        isVotRedesign || (device.type === 'mobile' && mhcVariant !== ExperimentVariant.VARIANT_A);

    if (showModernFooter) {
        return (
            <ErrorBoundary>
                <ModernFooter
                    apps={apps}
                    copyrightLinks={copyright.links}
                    device={device}
                    isMotors={category?.l1Category === 'cars-vans-motorbikes'}
                    isVotRedesign={isVotRedesign}
                    partners={globalSites.items}
                    sections={sections}
                />
            </ErrorBoundary>
        );
    }

    if (device.type === 'mobile') {
        sections.pop();
    }

    return (
        <ErrorBoundary>
            <FooterBlock {...qaAttribute('footer-container')}>
                <Container isSection>
                    <nav>
                        {device.type === 'mobile' && (
                            <>
                                <FooterAppLinks
                                    sections={apps}
                                    device={device}
                                    trackAndNavigate={trackAndNavigate}
                                />
                            </>
                        )}
                        <FooterLinks
                            sections={sections}
                            device={device}
                            trackAndNavigate={trackAndNavigate}
                        />
                        <Divider />
                        <Grid container>
                            <Grid item col={12} colL={3} colXl={3} offsetL={5} offsetXl={5}>
                                <FooterSocial />
                            </Grid>
                        </Grid>
                        <Divider />
                        <FooterCopyright links={copyright.links} />
                    </nav>
                </Container>
            </FooterBlock>
        </ErrorBoundary>
    );
};

const mapState = ({ baseConfig: { category, device } }: ShellState) => ({
    category,
    device,
});

const mapDispatch = (dispatch) => ({
    trackAndNavigate: (event: string, url: string, eventLabel?: string) => {
        dispatch(trackAndNavigate(event, url, eventLabel));
    },
});

export type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect(mapState, mapDispatch)(Footer);
